import type {
  BeatId,
  ResolvedEdit,
  SourcePack,
  StickerOption,
  Tick,
} from "@desk/core";
import { stickerIntervalCoveredByAllowedBeats } from "@desk/core";
import { packEndTick } from "./caption-preview.js";
import { onSubjectAnchor } from "./creative-fx.js";
import { durationTicksFor } from "./sticker-edit.js";

/** Retained (non-omitted, non-empty-trim) beats in resolved order. */
export function retainedBeatIds(resolved: ResolvedEdit): BeatId[] {
  return resolved.beatOrder.filter((id) => {
    const cut = resolved.cuts.get(id);
    if (!cut || cut.mode === "keep") return true;
    if (cut.mode === "omit") return false;
    if (cut.mode === "trim") {
      if (cut.inTick === undefined || cut.outTick === undefined) return false;
      return cut.outTick > cut.inTick;
    }
    return true;
  });
}

/** Story beats eligible as outgoing transition boundaries (not the final retained beat). */
export function transitionEligibleBeatIds(resolved: ResolvedEdit): BeatId[] {
  const retained = retainedBeatIds(resolved);
  if (retained.length <= 1) return [];
  return retained.slice(0, -1);
}

/** Prefer first retained beat; fall back to first pack beat. */
export function defaultStoryBeatId(
  pack: SourcePack,
  resolved: ResolvedEdit,
): BeatId | null {
  return retainedBeatIds(resolved)[0] ?? pack.beats[0]?.id ?? null;
}

/**
 * Deterministic SFX placement: start of the chosen story beat, clamped to
 * [0, sourceEnd-1] so final-beat starts remain valid.
 */
export function sfxTickForBeat(pack: SourcePack, beatId: BeatId): Tick {
  const sourceEnd = packEndTick(pack);
  const maxTick = Math.max(0, sourceEnd - 1);
  const beat = pack.beats.find((b) => b.id === beatId);
  const start = beat?.startTick ?? 0;
  return Math.max(0, Math.min(start, maxTick));
}

export type StickerPlacementDefaults = {
  startTick: Tick;
  endTick: Tick;
  anchorXMilli: number;
  anchorYMilli: number;
  scaleMilli: number;
  rotationMilli: number;
};

const DEFAULT_STICKER_SPAN_TICKS = 45;
const DEFAULT_ANCHOR_X = 780;
const DEFAULT_ANCHOR_Y = 180;

/**
 * Deterministic sticker timing/placement for simple mode.
 * Returns null when no legal interval exists within source bounds / allowedBeatIds.
 */
export function defaultStickerPlacement(
  pack: SourcePack,
  resolved: ResolvedEdit,
  sticker: StickerOption,
): StickerPlacementDefaults | null {
  const sourceEnd = packEndTick(pack);
  if (sourceEnd <= 0) return null;

  const retained = retainedBeatIds(resolved);
  const candidateBeats =
    sticker.allowedBeatIds?.length
      ? pack.beats.filter((b) => sticker.allowedBeatIds!.includes(b.id))
      : pack.beats.filter((b) => retained.includes(b.id) || retained.length === 0);

  const beat =
    candidateBeats.find((b) => retained.includes(b.id)) ??
    candidateBeats[0] ??
    null;
  if (!beat) return null;

  let startTick = beat.startTick;
  let endTick = Math.min(sourceEnd, Math.min(beat.endTick, startTick + DEFAULT_STICKER_SPAN_TICKS));
  if (endTick <= startTick) {
    endTick = Math.min(sourceEnd, beat.endTick);
  }
  if (endTick <= startTick) return null;

  if (sticker.allowedBeatIds?.length) {
    const allowed = pack.beats.filter((b) => sticker.allowedBeatIds!.includes(b.id));
    if (!stickerIntervalCoveredByAllowedBeats(startTick, endTick, allowed)) {
      const first = allowed[0];
      if (!first) return null;
      startTick = first.startTick;
      endTick = Math.min(first.endTick, startTick + DEFAULT_STICKER_SPAN_TICKS);
      if (endTick <= startTick) endTick = first.endTick;
      if (
        endTick <= startTick ||
        endTick > sourceEnd ||
        !stickerIntervalCoveredByAllowedBeats(startTick, endTick, allowed)
      ) {
        return null;
      }
    }
  }

  if (endTick > sourceEnd) return null;

  return { startTick, endTick, ...defaultStickerAnchor(sticker) };
}

/**
 * Shared anchor/scale/rotation for a sticker: wearable props anchor over the
 * subject (cat's head/face) instead of the corner. Used by both
 * `defaultStickerPlacement` (beat-anchored) and `playheadStickerPlacement`
 * (playhead-anchored) so the two placement strategies never disagree on
 * where/how big a sticker starts out.
 */
function defaultStickerAnchor(sticker: StickerOption): {
  anchorXMilli: number;
  anchorYMilli: number;
  scaleMilli: number;
  rotationMilli: number;
} {
  const onSubject = onSubjectAnchor(sticker.id, sticker.tags);
  return {
    anchorXMilli: onSubject?.anchorXMilli ?? DEFAULT_ANCHOR_X,
    anchorYMilli: onSubject?.anchorYMilli ?? DEFAULT_ANCHOR_Y,
    scaleMilli: sticker.defaultScaleMilli,
    rotationMilli: sticker.defaultRotationMilli,
  };
}

/**
 * Sticker timing/placement anchored at the editor PLAYHEAD ("scrub then tap"):
 * the window starts at `playheadSourceTick` and lasts `appearForSeconds`,
 * clamped to source bounds. Falls back to the beat-anchored
 * `defaultStickerPlacement` when the sticker is beat-restricted
 * (`allowedBeatIds`) and the playhead window isn't covered by those beats —
 * preserving today's behavior for arrow/frame stickers.
 */
export function playheadStickerPlacement(
  pack: SourcePack,
  resolved: ResolvedEdit,
  sticker: StickerOption,
  playheadSourceTick: Tick,
  appearForSeconds: number,
): StickerPlacementDefaults | null {
  const sourceEnd = packEndTick(pack);
  if (sourceEnd <= 0) return null;

  const startTick = Math.max(0, Math.min(playheadSourceTick, sourceEnd - 1));
  const endTick = Math.min(sourceEnd, startTick + durationTicksFor(appearForSeconds, pack.tickRate));
  if (endTick <= startTick) return null;

  if (sticker.allowedBeatIds?.length) {
    const allowed = pack.beats.filter((b) => sticker.allowedBeatIds!.includes(b.id));
    if (!stickerIntervalCoveredByAllowedBeats(startTick, endTick, allowed)) {
      return defaultStickerPlacement(pack, resolved, sticker);
    }
  }

  return { startTick, endTick, ...defaultStickerAnchor(sticker) };
}

/** Inclusive legal Fine-tune range for a sticker option within source / allowed beats. */
export type StickerFineTuneBounds = {
  minStart: Tick;
  maxStart: Tick;
  minEnd: Tick;
  maxEnd: Tick;
};

/**
 * Legal [start,end) authoring window for Fine tune.
 * Unconstrained: within [0, sourceEnd]. Constrained: within union of allowedBeatIds.
 */
export function stickerFineTuneBounds(
  pack: SourcePack,
  sticker: StickerOption,
): StickerFineTuneBounds | null {
  const sourceEnd = packEndTick(pack);
  if (sourceEnd <= 1) return null;

  let windowStart = 0;
  let windowEnd = sourceEnd;

  if (sticker.allowedBeatIds?.length) {
    const allowed = pack.beats.filter((b) => sticker.allowedBeatIds!.includes(b.id));
    if (allowed.length === 0) return null;
    const sorted = [...allowed].sort((a, b) => a.startTick - b.startTick);
    // Use merged union span for slider bounds; emit still requires full coverage.
    windowStart = Math.min(...sorted.map((b) => b.startTick));
    windowEnd = Math.max(...sorted.map((b) => b.endTick));
    windowEnd = Math.min(windowEnd, sourceEnd);
    if (windowEnd <= windowStart) return null;
  }

  if (windowEnd - windowStart < 1) return null;
  return {
    minStart: windowStart,
    maxStart: windowEnd - 1,
    minEnd: windowStart + 1,
    maxEnd: windowEnd,
  };
}

/** Clamp a Fine-tune sticker interval into legal bounds; null when no legal interval. */
export function clampStickerFineTuneInterval(
  pack: SourcePack,
  sticker: StickerOption,
  startTick: Tick,
  endTick: Tick,
): { startTick: Tick; endTick: Tick } | null {
  const bounds = stickerFineTuneBounds(pack, sticker);
  if (!bounds) return null;
  let start = Math.max(bounds.minStart, Math.min(startTick, bounds.maxStart));
  let end = Math.max(bounds.minEnd, Math.min(endTick, bounds.maxEnd));
  if (end <= start) {
    end = Math.min(bounds.maxEnd, start + 1);
  }
  if (end <= start) return null;

  if (sticker.allowedBeatIds?.length) {
    const allowed = pack.beats.filter((b) => sticker.allowedBeatIds!.includes(b.id));
    if (!stickerIntervalCoveredByAllowedBeats(start, end, allowed)) {
      for (const beat of allowed) {
        const s = Math.max(bounds.minStart, beat.startTick);
        const e = Math.min(bounds.maxEnd, beat.endTick);
        if (e <= s) continue;
        const snappedEnd = Math.min(e, Math.max(s + 1, Math.min(s + DEFAULT_STICKER_SPAN_TICKS, e)));
        if (stickerIntervalCoveredByAllowedBeats(s, snappedEnd, allowed)) {
          return { startTick: s, endTick: snappedEnd };
        }
      }
      return null;
    }
  }
  return { startTick: start, endTick: end };
}
